import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  query,
  where,
  getFirestore,
  onSnapshot,
  updateDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase";
import AgendaPage from "../components/Agenda";
import LoadingComLayout from "../components/LoadingComLayout";
import { useToast } from "../components/ToastProvider";
import { 
  LuClock4, LuCalendar, LuSave, 
  LuLock, LuLockOpen, LuTriangleAlert // Ícone de alerta adicionado
} from "react-icons/lu";

const db = getFirestore(app);
const auth = getAuth(app);

/* ======================= ESTILOS ======================= */
const LoadingContainer = styled.div`
  display: flex; justify-content: center; align-items: center; height: 80vh; width: 100%;
`;

const Container = styled.main`
  flex: 1;
  padding: 2rem 4rem;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 1024px) { padding: 1.5rem; }
  @media (max-width: 768px) { padding: 1rem; }
`;

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: 2.5fr 1fr;
  gap: 1.5rem;
  align-items: start;
  height: 100%;

  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const Section = styled.section`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  &.calendar-section { min-height: 650px; height: 100%; }
  &.schedule-section { max-height: 800px; display: flex; flex-direction: column; }
`;

const SectionHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: .6rem; padding: 1rem 1.1rem; border-bottom: 1px solid #eef2fb; 
  
  .title-group {
    display: flex; align-items: center; gap: 0.6rem;
    color: #1f1a40; font-weight: 700;
    svg { color:#3a79ed; }
  }
`;

const SectionBody = styled.div`
  padding: 1rem 1.1rem; flex: 1; display: flex; flex-direction: column; position: relative; background: #fff; overflow: auto;
  &.no-padding { padding: 0; }
  &.padded { padding: 1.5rem; gap: 1rem; overflow: hidden; }
`;

/* === Scroll Area da Lista de Dias === */
const ScrollableDays = styled.div`
  flex: 1; overflow-y: auto; padding-right: 0.5rem; margin-right: -0.5rem; display: flex; flex-direction: column;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
`;

const DayRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; padding: 0.85rem 0; border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const DayLeft = styled.div`
  display: flex; align-items: center; gap: 0.8rem;
  span { text-transform: capitalize; font-weight: 600; color: #334155; font-size: 0.95rem; }
`;

/* Toggle Button */
const Toggle = styled.button`
  width: 42px; height: 24px; border-radius: 99px; border: none;
  background: ${p => p.active ? "#3a79ed" : "#cbd5e1"};
  position: relative; cursor: pointer; transition: background 0.2s; flex-shrink: 0;

  &::after {
    content: ""; position: absolute; top: 2px;
    left: ${p => p.active ? "20px" : "2px"};
    width: 20px; height: 20px; background: #fff; border-radius: 50%;
    transition: left 0.2s cubic-bezier(0.4, 0.0, 0.2, 1); box-shadow: 0 1px 2px rgba(0,0,0,0.2);
  }
`;

const TimeControls = styled.div`
  display: flex; align-items: center; gap: 0.5rem;
  .separator { color: #cbd5e1; font-size: 0.8rem; }
`;

const StyledSelect = styled.select`
  padding: 0.35rem 0.5rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; background-color: #fff;
  font-size: 0.9rem; color: #1e293b; outline: none; cursor: pointer; transition: all 0.2s; min-width: 75px;
  &:focus { border-color: #3a79ed; box-shadow: 0 0 0 2px rgba(58, 121, 237, 0.1); }
  &:hover { border-color: #cbd5e1; }
`;

const BadgeClosed = styled.div`
  background: #f1f5f9; color: #94a3b8; font-size: 0.8rem; font-weight: 600; padding: 0.3rem 0.8rem; border-radius: 0.5rem;
`;

const SaveButton = styled.button`
  margin-top: 1rem; width: 100%; padding: 0.9rem; background-color: #3a79ed; color: white;
  font-size: 1rem; font-weight: 600; border: none; border-radius: 0.75rem; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 0.5rem; transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(58, 121, 237, 0.2);
  &:hover { background-color: #2563eb; transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
`;

const ActionButton = styled.button`
  padding: 0.4rem 1rem; font-weight: 600; border: none; border-radius: 0.375rem;
  cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
  font-size: 0.85rem; transition: 0.2s;
  background-color: ${(props) => props.bg}; color: ${(props) => props.color};
  &:hover { filter: brightness(0.95); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// === ESTILOS DO MODAL ===
const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); z-index: 999;
  display: flex; align-items: center; justify-content: center; padding: 20px;
`;
const ModalContent = styled.div`
  background: white; padding: 2rem; border-radius: 1rem; width: 100%; max-width: 450px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15); animation: fadeIn 0.2s ease-out;
  text-align: center;
`;
const ModalActions = styled.div`
  display: flex; justify-content: center; gap: 10px; margin-top: 25px;
`;
const WarningIcon = styled.div`
  width: 60px; height: 60px; background: #fee2e2; color: #ef4444;
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1.5rem auto; font-size: 2rem;
`;

/* ======================= LÓGICA ======================= */

export default function AgendaHorarios() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [barbeariaId, setBarbeariaId] = useState(null);
  const [agendaAberta, setAgendaAberta] = useState(false);
  
  // Estado para controlar o modal de confirmação
  const [modalOpen, setModalOpen] = useState(false);

  const [form, setForm] = useState({
    horarios: {
      domingo: { aberto: false, inicio: "", fim: "" },
      segunda: { aberto: true, inicio: "09:00", fim: "22:00" },
      terca: { aberto: true, inicio: "09:00", fim: "22:00" },
      quarta: { aberto: true, inicio: "09:00", fim: "22:00" },
      quinta: { aberto: true, inicio: "09:00", fim: "22:00" },
      sexta: { aberto: true, inicio: "09:00", fim: "22:00" },
      sabado: { aberto: false, inicio: "", fim: "" },
    },
  });

  const { success, error: toastError } = useToast();
  const diasSemana = ["domingo", "segunda", "terca", "quarta", "quinta", "sexta", "sabado"];

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) { setLoading(false); return; }
      const uid = user.uid;
      try {
        const q = query(collection(db, "barbearia"), where("criadorUid", "==", uid));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          const id = snapshot.docs[0].id;
          setBarbeariaId(id);
          await carregarHorarios(id);

          onSnapshot(doc(db, "barbearia", id), (docSnap) => {
             if(docSnap.exists()) {
                 const data = docSnap.data();
                 setAgendaAberta(data.agendaAberta === true);
             }
          });
        }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    });
    return () => unsubscribeAuth();
  }, []);

  const carregarHorarios = async (id) => {
    const horariosRef = collection(db, `barbearia/${id}/horarioFuncionamento`);
    const snapshot = await getDocs(horariosRef);
    const horarios = {};
    snapshot.forEach((docx) => {
      const data = docx.data();
      horarios[docx.id] = {
        aberto: data.aberto,
        inicio: data.horarioInicio,
        fim: data.horarioTermino,
      };
    });
    setForm((prev) => ({ ...prev, horarios: { ...prev.horarios, ...horarios } }));
  };

  const salvarHorarios = async () => {
    if (!barbeariaId) return;
    setSaving(true);
    try {
      const horariosRef = collection(db, `barbearia/${barbeariaId}/horarioFuncionamento`);
      for (const dia in form.horarios) {
        await setDoc(doc(horariosRef, dia), {
          aberto: form.horarios[dia].aberto,
          horarioInicio: form.horarios[dia].inicio,
          horarioTermino: form.horarios[dia].fim,
        });
      }
      success("Horários salvos com sucesso!");
    } catch (e) { toastError("Erro ao salvar."); } finally { setSaving(false); }
  };

  // === LÓGICA DO TOGGLE COM MODAL ===

  const handleClickToggle = () => {
      if (agendaAberta) {
          // Se está aberta e vai fechar, mostra o aviso
          setModalOpen(true);
      } else {
          // Se está fechada e vai abrir, abre direto
          executarAlteracaoAgenda(true);
      }
  };

  const confirmarFechamento = () => {
      executarAlteracaoAgenda(false);
      setModalOpen(false);
  };

  const executarAlteracaoAgenda = async (novoStatus) => {
    if (!barbeariaId) return;
    try {
        await updateDoc(doc(db, "barbearia", barbeariaId), { 
            agendaAberta: novoStatus 
        });
    } catch (error) {
        toastError("Erro ao atualizar status da agenda.");
        console.error(error);
    }
  };

  const gerarHorarios = () => {
    const lista = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) { 
        const hora = String(h).padStart(2, "0");
        const minuto = String(m).padStart(2, "0");
        lista.push(`${hora}:${minuto}`);
      }
    }
    return lista;
  };
  const horariosOptions = gerarHorarios();

  const updateDia = (dia, field, value) => {
    setForm(prev => ({
      ...prev,
      horarios: {
        ...prev.horarios,
        [dia]: { ...prev.horarios[dia], [field]: value }
      }
    }));
  };

  const toggleDia = (dia) => {
    const atual = form.horarios[dia];
    const novoEstado = !atual.aberto;
    setForm(prev => ({
      ...prev,
      horarios: {
        ...prev.horarios,
        [dia]: {
          ...atual,
          aberto: novoEstado,
          inicio: novoEstado && !atual.inicio ? "09:00" : atual.inicio,
          fim: novoEstado && !atual.fim ? "18:00" : atual.fim,
        }
      }
    }));
  };

  if (loading) return <LoadingComLayout />;

  return (
    <>
      {loading ? (
        <LoadingContainer>
            <LoadingComLayout />
        </LoadingContainer>
      ) : (
      <Container>
        <PageGrid>
          {/* === ESQUERDA: CALENDÁRIO === */}
          <Section className="calendar-section">
            <SectionHeader>
                <div className="title-group">
                    <LuCalendar /> Meu Calendário
                </div>
                
                {/* BOTÃO CHAMA A FUNÇÃO DE VERIFICAÇÃO */}
                <ActionButton 
                    bg={agendaAberta ? "#fee2e2" : "#dcfce7"} 
                    color={agendaAberta ? "#ef4444" : "#16a34a"}
                    onClick={handleClickToggle}
                >
                    {agendaAberta 
                        ? <><LuLock size={16}/> Fechar Agenda</> 
                        : <><LuLockOpen size={16}/> Abrir Agenda</>
                    }
                </ActionButton>
            </SectionHeader>
            
            <SectionBody>
              <div style={{ width: '100%', height: '100%', minHeight: '600px' }}>
                <AgendaPage />
              </div>
            </SectionBody>
          </Section>

          {/* === DIREITA: HORÁRIOS === */}
          <Section className="schedule-section">
            <SectionHeader>
              <div className="title-group">
                  <LuClock4 /> Horário de Funcionamento
              </div>
            </SectionHeader>

            <SectionBody className="padded">
              <ScrollableDays>
                {diasSemana.map((dia) => {
                  const d = form.horarios?.[dia];
                  return (
                    <DayRow key={dia}>
                      <DayLeft>
                        <Toggle active={d.aberto} onClick={() => toggleDia(dia)} />
                        <span>{dia}</span>
                      </DayLeft>
                      {d.aberto ? (
                        <TimeControls>
                          <StyledSelect
                            value={d.inicio}
                            onChange={(e) => updateDia(dia, 'inicio', e.target.value)}
                          >
                            {horariosOptions.map(h => <option key={h} value={h}>{h}</option>)}
                          </StyledSelect>
                          <span className="separator">-</span>
                          <StyledSelect
                            value={d.fim}
                            onChange={(e) => updateDia(dia, 'fim', e.target.value)}
                          >
                            {horariosOptions.map(h => <option key={h} value={h}>{h}</option>)}
                          </StyledSelect>
                        </TimeControls>
                      ) : (
                        <BadgeClosed>Fechado</BadgeClosed>
                      )}
                    </DayRow>
                  );
                })}
              </ScrollableDays>

              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '1rem' }}>
                <SaveButton onClick={salvarHorarios} disabled={saving}>
                  {saving ? "Salvando..." : <><LuSave size={18} /> Salvar Horários</>}
                </SaveButton>
              </div>
            </SectionBody>
          </Section>
        </PageGrid>

        {/* === MODAL DE AVISO === */}
        {modalOpen && (
            <ModalOverlay onClick={() => setModalOpen(false)}>
                <ModalContent onClick={e => e.stopPropagation()}>
                    <WarningIcon>
                        <LuTriangleAlert />
                    </WarningIcon>
                    <h3 style={{margin: '0 0 10px 0', color: '#1f1a40'}}>Fechar Agenda?</h3>
                    <p style={{color: '#64748b', fontSize: '0.95rem', lineHeight: '1.5'}}>
                        Ao confirmar, sua agenda ficará bloqueada e 
                        <strong style={{color: '#ef4444'}}> seus clientes não conseguirão fazer novos agendamentos</strong> pelo site.
                    </p>
                    <ModalActions>
                        <ActionButton 
                            bg="#f3f4f6" color="#374151" 
                            onClick={() => setModalOpen(false)}
                            style={{padding: '0.6rem 1.2rem'}}
                        >
                            Cancelar
                        </ActionButton>
                        <ActionButton 
                            bg="#ef4444" color="#fff" 
                            onClick={confirmarFechamento}
                            style={{padding: '0.6rem 1.2rem'}}
                        >
                            Sim, Fechar Agenda
                        </ActionButton>
                    </ModalActions>
                </ModalContent>
            </ModalOverlay>
        )}

      </Container>
      )}
    </>
  );
}